export type UserRole = 'admin' | 'sales' | 'sales-manager' | 'ops';

export interface RolePermissions {
  // Dashboard
  canViewDashboard: boolean;
  canViewAllDashboard: boolean; // Admin sees all, others see own/team
  
  // Customers
  canViewCustomers: boolean;
  canViewAllCustomers: boolean; // Admin/ops see all, sales see own
  canCreateCustomers: boolean;
  canEditCustomers: boolean;
  
  // Queries
  canViewQueries: boolean;
  canViewAllQueries: boolean; // Admin/ops see all, sales see own, manager sees team
  canCreateQueries: boolean;
  canEditQueries: boolean;
  
  // Followups
  canViewFollowups: boolean;
  canViewAllFollowups: boolean;
  canCreateFollowups: boolean;
  canEditFollowups: boolean;
  
  // Bookings
  canViewBookings: boolean;
  canViewAllBookings: boolean;
  canCreateBookings: boolean;
  canEditBookings: boolean;
  
  // Activities
  canViewActivities: boolean;
  canCreateActivities: boolean;
  canEditActivities: boolean;
  canDeleteActivities: boolean;
  
  // Destinations
  canViewDestinations: boolean;
  canCreateDestinations: boolean;
  canEditDestinations: boolean;
  canDeleteDestinations: boolean;
  
  // Packages
  canViewPackages: boolean;
  canViewAllPackages: boolean;
  canCreatePackages: boolean;
  canEditPackages: boolean;
  canDeletePackages: boolean;

  // Hotels
  canViewHotels: boolean;
  canManageHotels: boolean;
  
  // Attendance
  canViewAttendance: boolean;
  canViewAllAttendance: boolean; // Admin sees all, others see own
  canMarkAttendance: boolean;
  
  // Users/Team Management
  canViewUsers: boolean;
  canManageUsers: boolean; // Create, edit, delete users
  canViewTeam: boolean; // View team members (for managers)

  // Telecalling
  canViewTelecalling: boolean;
  canManageTelecalling: boolean;

  // MCP Admin
  canViewMcpAdmin: boolean;
  canManageMcpAdmin: boolean;
}

export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  admin: {
    canViewDashboard: true,
    canViewAllDashboard: true,
    canViewCustomers: true,
    canViewAllCustomers: true,
    canCreateCustomers: true,
    canEditCustomers: true,
    canViewQueries: true,
    canViewAllQueries: true,
    canCreateQueries: true,
    canEditQueries: true,
    canViewFollowups: true,
    canViewAllFollowups: true,
    canCreateFollowups: true,
    canEditFollowups: true,
    canViewBookings: true,
    canViewAllBookings: true,
    canCreateBookings: true,
    canEditBookings: true,
    canViewActivities: true,
    canCreateActivities: true,
    canEditActivities: true,
    canDeleteActivities: true,
    canViewDestinations: true,
    canCreateDestinations: true,
    canEditDestinations: true,
    canDeleteDestinations: true,
    canViewPackages: true,
    canViewAllPackages: true,
    canCreatePackages: true,
    canEditPackages: true,
    canDeletePackages: true,
    canViewHotels: true,
    canManageHotels: true,
    canViewAttendance: true,
    canViewAllAttendance: true,
    canMarkAttendance: true,
    canViewUsers: true,
    canManageUsers: true,
    canViewTeam: true,
    canViewTelecalling: true,
    canManageTelecalling: true,
    canViewMcpAdmin: true,
    canManageMcpAdmin: true,
  },
  'sales': {
    canViewDashboard: true,
    canViewAllDashboard: false, // Only own data
    canViewCustomers: true,
    canViewAllCustomers: false, // Only own customers
    canCreateCustomers: true,
    canEditCustomers: true,
    canViewQueries: true,
    canViewAllQueries: false, // Only own queries
    canCreateQueries: true,
    canEditQueries: true,
    canViewFollowups: true,
    canViewAllFollowups: false, // Only own followups
    canCreateFollowups: true,
    canEditFollowups: true,
    canViewBookings: true,
    canViewAllBookings: false, // Only own bookings
    canCreateBookings: true,
    canEditBookings: true,
    canViewActivities: false,
    canCreateActivities: false,
    canEditActivities: false,
    canDeleteActivities: false,
    canViewDestinations: false,
    canCreateDestinations: false,
    canEditDestinations: false,
    canDeleteDestinations: false,
    canViewPackages: true,
    canViewAllPackages: false,
    canCreatePackages: false,
    canEditPackages: true,
    canDeletePackages: false,
    canViewHotels: true,
    canManageHotels: false,
    canViewAttendance: true,
    canViewAllAttendance: false, // Only own attendance
    canMarkAttendance: true,
    canViewUsers: false,
    canManageUsers: false,
    canViewTeam: false,
    canViewTelecalling: true,
    canManageTelecalling: false,
    canViewMcpAdmin: false,
    canManageMcpAdmin: false,
  },
  'sales-manager': {
    canViewDashboard: true,
    canViewAllDashboard: false, // Own + team data
    canViewCustomers: true,
    canViewAllCustomers: false, // Own + team customers
    canCreateCustomers: true,
    canEditCustomers: true,
    canViewQueries: true,
    canViewAllQueries: false, // Own + team queries
    canCreateQueries: true,
    canEditQueries: true,
    canViewFollowups: true,
    canViewAllFollowups: false, // Own + team followups
    canCreateFollowups: true,
    canEditFollowups: true,
    canViewBookings: true,
    canViewAllBookings: false, // Own + team bookings
    canCreateBookings: true,
    canEditBookings: true,
    canViewActivities: false,
    canCreateActivities: false,
    canEditActivities: false,
    canDeleteActivities: false,
    canViewDestinations: true,
    canCreateDestinations: false,
    canEditDestinations: false,
    canDeleteDestinations: false,
    canViewPackages: true,
    canViewAllPackages: false,
    canCreatePackages: false,
    canEditPackages: false,
    canDeletePackages: false,
    canViewHotels: true,
    canManageHotels: false,
    canViewAttendance: true,
    canViewAllAttendance: false, // Own + team attendance
    canMarkAttendance: true,
    canViewUsers: false,
    canManageUsers: false,
    canViewTeam: true, // Can view team members
    canViewTelecalling: true,
    canManageTelecalling: false,
    canViewMcpAdmin: false,
    canManageMcpAdmin: false,
  },
  'ops': {
    canViewDashboard: true,
    canViewAllDashboard: false, // Own + team data
    canViewCustomers: true,
    canViewAllCustomers: false, // Own + team customers
    canCreateCustomers: true,
    canEditCustomers: true,
    canViewQueries: true,
    canViewAllQueries: false, // Own + team queries
    canCreateQueries: true,
    canEditQueries: true,
    canViewFollowups: true,
    canViewAllFollowups: false, // Own + team followups
    canCreateFollowups: true,
    canEditFollowups: true,
    canViewBookings: true,
    canViewAllBookings: false, // Own + team bookings
    canCreateBookings: true,
    canEditBookings: true,
    canViewActivities: false,
    canCreateActivities: true,
    canEditActivities: true,
    canDeleteActivities: true,
    canViewDestinations: true,
    canCreateDestinations: true,
    canEditDestinations: false,
    canDeleteDestinations: false,
    canViewPackages: true,
    canViewAllPackages: false,
    canCreatePackages: true,
    canEditPackages: false,
    canDeletePackages: false,
    canViewHotels: true,
    canManageHotels: true,
    canViewAttendance: true,
    canViewAllAttendance: false, // Own + team attendance
    canMarkAttendance: true,
    canViewUsers: false,
    canManageUsers: false,
    canViewTeam: true, // Can view team members
    canViewTelecalling: true,
    canManageTelecalling: false,
    canViewMcpAdmin: false,
    canManageMcpAdmin: false,
  },
};
